// Wohler Chemical — Unified Compounds Database
// Aggregates all modular compound data files into a single API
// Each compound contains graduate-school-level research data:
//   - Pharmacology & receptor targets
//   - Lab-scale synthesis procedures
//   - Kg-scale manufacturing plans (using Wohler standard solvents:
//     Ethyl Acetate, THF, Hexane, Heptane, Pentane, Ethanol, Methanol, IPA, Acetone)
//   - Safety / EHS assessment
//   - Published research references
//   - Wohler Scoring (Benefit, Harm, Net Value, Evidence Confidence)
//   - Next-steps / to-do research tasks

import { tryptamines } from "./compounds-tryptamines";
import { mitragynineCompounds } from "./compounds-mitragynine";
import { dissociativeCompounds } from "./compounds-dissociatives";
import { opioidIbogaCompounds } from "./compounds-opioid-iboga";
import { cognitiveCompounds } from "./compounds-cognitive";
import { gabaPainCompounds } from "./compounds-gaba-pain";
import { entactogenEdEpitalonCompounds } from "./compounds-entactogens-ed-epitalon";
import { todoCompounds } from "./compounds-todo-additions";
import { masterStubCompounds } from "./compounds-master-stubs";
import { gabapentinoidCompounds } from "./compounds-gabapentinoids";
import { tryptamineResearchProfiles } from "./compounds-research-tryptamines";
import { mitragynineResearchProfiles } from "./compounds-research-mitragynine";
import { ibogaResearchProfiles } from "./compounds-research-iboga";
import { opioidResearchProfiles } from "./compounds-research-opioid";
import { flagshipResearchProfiles } from "./compounds-research-flagship";
import { enhancedContentBatch01 } from "./compounds-enhanced-content";
import { excelImportCompounds } from "./compounds-excel-imports";
import { categories, getCategory as _getCategory } from "./categories";
import { loadOverrides, applyOverrides } from "./localOverrides";

// Unified flat database of all compounds, keyed by compound ID
export const compoundsDB = {
  ...tryptamines,
  ...mitragynineCompounds,
  ...dissociativeCompounds,
  ...opioidIbogaCompounds,
  ...cognitiveCompounds,
  ...gabaPainCompounds,
  ...entactogenEdEpitalonCompounds,
  ...todoCompounds,
  ...masterStubCompounds,
  ...gabapentinoidCompounds,
  // Phase 6 research profiles — override stubs with full research entries
  ...tryptamineResearchProfiles,
  ...mitragynineResearchProfiles,
  ...ibogaResearchProfiles,
  ...opioidResearchProfiles,
  // Flagship demo compound — spread LAST to override all stubs with complete data
  ...flagshipResearchProfiles
};

// Merge Excel-imported data field-by-field (NOT a full replace) so leaner Excel
// entries enrich/override specific fields without wiping out rich nested sections
// (labSynthesis, kgScaleSynthesis, safety, publishedResearch, wohlerScoring,
// legalIp, journalEntries, etc.) that only exist in the fuller research profiles.
// Bug fix: previously `...excelImportCompounds` was spread at the top level, which
// fully replaced any compound sharing an id (e.g. the flagship "4-aco-met" record
// lost all of its synthesis/safety/scoring data because the Excel version never
// had those keys).
Object.keys(excelImportCompounds).forEach(id => {
  if (compoundsDB[id]) {
    compoundsDB[id] = {
      ...compoundsDB[id],
      ...excelImportCompounds[id]
    };
  } else {
    compoundsDB[id] = excelImportCompounds[id];
  }
});

// Merge enhanced content (Batch 01) into existing compounds without wiping their data
Object.keys(enhancedContentBatch01).forEach(id => {
  if (compoundsDB[id]) {
    compoundsDB[id] = {
      ...compoundsDB[id],
      ...enhancedContentBatch01[id]
    };
  }
});

// Apply any localStorage overrides (from the in-browser Import tool) — always last
const _runtimeOverrides = loadOverrides();
if (Object.keys(_runtimeOverrides).length > 0) {
  const patched = applyOverrides(compoundsDB, _runtimeOverrides);
  Object.assign(compoundsDB, patched);
}

// Backward-compatible alias — existing pages import `compounds`
export const compounds = compoundsDB;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getCompound(id) {
  return compoundsDB[id] || null;
}

export function getCategory(id) {
  return _getCategory(id);
}

export function getAllCompounds() {
  return Object.keys(compoundsDB).map(id => ({
    id,
    ...compoundsDB[id]
  }));
}

export function getCompoundsByCategory(categoryId) {
  const category = _getCategory(categoryId);
  if (!category) return [];
  return category.compoundIds
    .map(id => compoundsDB[id] ? { id, ...compoundsDB[id] } : null)
    .filter(Boolean);
}

export function searchCompounds(query) {
  if (!query) return [];
  const q = query.toLowerCase();
  return getAllCompounds().filter(c => {
    const name = (c.name || "").toLowerCase();
    const aliases = (c.aliases || []).join(" ").toLowerCase();
    const iupac = (c.iupacName || "").toLowerCase();
    const desc = (c.description || "").toLowerCase();
    const id = c.id.toLowerCase();
    return (
      name.includes(q) ||
      aliases.includes(q) ||
      iupac.includes(q) ||
      desc.includes(q) ||
      id.includes(q)
    );
  });
}

// Returns compounds flagged for additional research / scoring
export function getTodoCompounds() {
  return getAllCompounds().filter(c => {
    if (c.status === "RESEARCH-NEEDED" || c.status === "HOLD" || c.status === "TODO") return true;
    if (!c.wohlerScoring || c.wohlerScoring.evidenceConfidence === "LOW") return true;
    if (c.nextSteps && c.nextSteps.length > 0) return true;
    return false;
  });
}

// Returns compounds tiered by priority
export function getCompoundsByTier(tier) {
  return getAllCompounds().filter(c => c.priorityTier === tier);
}

// Counts
export function getStats() {
  const all = getAllCompounds();
  return {
    total: all.length,
    byCategory: categories.map(cat => ({
      id: cat.id,
      name: cat.name,
      count: cat.compoundIds.length
    })),
    byStatus: {
      go: all.filter(c => c.status === "GO" || c.status === "ACTIVE").length,
      hold: all.filter(c => c.status === "HOLD").length,
      kill: all.filter(c => c.status === "KILL").length,
      research: all.filter(c => c.status === "RESEARCH-NEEDED" || c.status === "TODO").length
    }
  };
}

// Wohler standard solvent inventory (available at scale)
export const STANDARD_SOLVENTS = [
  "Ethyl Acetate (EtOAc)",
  "Tetrahydrofuran (THF)",
  "Hexane",
  "Heptane",
  "Pentane",
  "Ethanol (EtOH)",
  "Methanol (MeOH)",
  "Isopropanol (IPA)",
  "Acetone"
];

export { categories };

// Default export for convenience
const api = {
  compoundsDB,
  categories,
  getCompound,
  getCategory,
  getAllCompounds,
  getCompoundsByCategory,
  searchCompounds,
  getTodoCompounds,
  getCompoundsByTier,
  getStats,
  STANDARD_SOLVENTS
};

export default api;